/**
 * One workflow (ADR-0025): steps grouped by when they're due, with who does each and how it ticks.
 * Read and review steps tick themselves from the Vault; uploads take a file; approvals take Yes or
 * Not yet with a note; automatic checks tick when DocVault can see the work is done.
 */
import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { api, type WfDetail, type WfStep } from "../../lib/api";
import { useStore } from "../../lib/store";
import { fmt } from "../../lib/format";
import { Loading } from "../../components/bits";
import { daysBetween } from "../../../../shared/agreements";

const KIND_WORD: Record<WfStep["kind"], string> = { task: "Task", read: "Read", upload: "Upload", approval: "Approval", auto: "Auto check", form: "Form", review: "Review" };
const KIND_HELP: [string, string][] = [["Task", "Someone ticks it"], ["Read", "Ticks itself when the person acknowledges the document"], ["Upload", "Ticks itself when the file is added; the file is kept with the step"],
  ["Approval", "Whoever the step goes to (shown on the step) says Yes or Not yet"], ["Auto check", "DocVault ticks it when it can see the work is done"], ["Review", "Ticks itself when the owner marks the document Reviewed, no changes, or publishes a revision"]];

export function WorkflowPage() {
  const { id } = useParams();
  const { toast } = useStore();
  const [d, setD] = useState<(WfDetail & { redirect?: string }) | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = () => api.workflow(id!).then(setD, (e: Error) => setErr(e.message));
  useEffect(() => { setD(null); void load(); }, [id]);
  const run = async (fn: () => Promise<unknown>, ok?: string) => { try { await fn(); if (ok) toast(ok); await load(); } catch (e) { toast((e as Error).message); } };
  if (err) return <div className="card empty view"><h3>This workflow didn't load</h3>{err}<div style={{ marginTop: 10 }}><Link className="doclink" to="/workflows">Back to Workflows</Link></div></div>;
  if (!d) return <Loading />;
  if (d.redirect) return <Navigate to={d.redirect} replace />;
  const w = d.workflow;
  const start = w.started_at?.slice(0, 10) ?? null;
  const groups = groupSteps(d.steps, w.kind === "employee_offboarding" ? w.person?.leaving_on ?? start : w.person?.starts_on ?? start, w.kind);
  const done = d.steps.filter((s) => s.done_at || s.skipped).length;
  const who = new Map<string, number>();
  for (const s of d.steps) if (!s.done_at && !s.skipped) who.set(s.assignee_label, (who.get(s.assignee_label) ?? 0) + 1);
  const sub = w.person ? [w.person.title, `at ${w.company_name}`, w.person.starts_on && w.kind === "employee_onboarding" && `starts ${fmt(w.person.starts_on)}`,
    w.person.leaving_on && w.kind === "employee_offboarding" && `last day ${fmt(w.person.leaving_on)}`, w.person.manager && `manager ${w.person.manager}`].filter(Boolean).join(" · ") : w.company_name;

  return (
    <div className="view">
      <div className="hgroup">
        <div>
          <div className="eyebrow">Workflows · {w.kind_label}</div>
          <h1 className="title">{w.person?.name ?? w.title}</h1>
          <div className="lede">{sub}{w.started_auto ? " · started automatically" : ""}{w.status !== "open" ? ` · ${w.status === "done" ? "finished" : "canceled"} ${w.finished_at ? fmt(w.finished_at) : ""}` : ""}</div>
        </div>
        {d.permissions.manage && w.status === "open" && <div className="row-btns"><button className="btn btn-ghost" onClick={() => { if (window.confirm(`Cancel ${w.title}? The steps stay in the record.`)) void run(() => api.wfCancel(w.id), "Canceled."); }}>Cancel workflow</button></div>}
      </div>
      <div className="x-two">
        <div className="card x-card">
          <div className="ev-srch"><h3>Steps</h3><span className="wf-prog"><span className="bar"><span style={{ width: `${d.steps.length ? (100 * done) / d.steps.length : 0}%` }} /></span>{done} of {d.steps.length}</span></div>
          {groups.map((g) => (
            <div key={g.label}>
              <div className="wf-grp">{g.label}</div>
              {g.steps.map((s) => <StepRow key={s.id} s={s} run={run} />)}
            </div>
          ))}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="card x-card">
            <h3>Who has what</h3>
            {[...who.entries()].map(([k, n]) => <div key={k} className="x-row"><b>{k}</b><span className="cap">{n} open</span></div>)}
            {!who.size && <div className="cap">Nothing left.</div>}
            <div className="cap" style={{ marginTop: 8 }}>Each step is in that person's My tasks with its due date.</div>
          </div>
          <div className="card x-card">
            <h3>Kinds of step</h3>
            <dl className="x-dl" style={{ gridTemplateColumns: "90px 1fr" }}>{KIND_HELP.map(([k, v]) => [<dt key={k}>{k}</dt>, <dd key={`${k}d`}>{v}</dd>])}</dl>
          </div>
        </div>
      </div>
    </div>
  );
}

function groupSteps(steps: WfStep[], dayZero: string | null, kind: string) {
  if (kind === "policy_review" || !dayZero) return [{ label: kind === "policy_review" ? "EACH PUBLISHED DOCUMENT" : "STEPS", steps }];
  const g: { label: string; steps: WfStep[] }[] = [{ label: kind === "employee_offboarding" ? "BEFORE THE LAST DAY" : "BEFORE DAY ONE", steps: [] }, { label: kind === "employee_offboarding" ? "ON THE LAST DAY" : "FIRST WEEK", steps: [] },
    { label: kind === "employee_offboarding" ? "AFTER" : "FIRST MONTH", steps: [] }, { label: "LATER", steps: [] }];
  for (const s of steps) {
    const n = s.due_on ? daysBetween(dayZero, s.due_on) : 0;
    const i = kind === "employee_offboarding" ? (n < 0 ? 0 : n === 0 ? 1 : 2) : n < 0 ? 0 : n <= 7 ? 1 : n <= 31 ? 2 : 3;
    g[i]!.steps.push(s);
  }
  return g.filter((x) => x.steps.length);
}

function StepRow({ s, run }: { s: WfStep; run: (fn: () => Promise<unknown>, ok?: string) => Promise<void> }) {
  const [note, setNote] = useState("");
  const [asking, setAsking] = useState<"" | "not_yet" | "skip">("");
  const done = !!s.done_at;
  const state = done ? (s.done_auto ? "auto" : "done") : s.skipped ? "skip" : "open";
  const upload = async (f: File | undefined) => {
    if (!f) return;
    const buf = new Uint8Array(await f.arrayBuffer());
    let bin = ""; for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
    await run(() => api.wfStepFile(s.id, { file_name: f.name, content_base64: btoa(bin) }), "Added. The step is done.");
  };
  const manual = s.kind === "task" || s.kind === "form";
  return (
    <div className={`wf-step ${done || s.skipped ? "done" : ""}`}>
      {manual && s.can_tick
        ? <button className={`wf-box ${state}`} aria-label={done ? `Untick ${s.title}` : `Tick ${s.title}`} onClick={() => void run(() => api.wfStep(s.id, { done: !done }))}>{done ? "✓" : ""}</button>
        : <span className={`wf-box ${state}`} aria-hidden="true">{done ? "✓" : s.skipped ? "–" : ""}</span>}
      <div>
        <b>{s.document ? <Link className="doclink" to={`/doc/${s.document.id}`}>{s.title}</Link> : s.title}</b><span className="wf-kind">{KIND_WORD[s.kind]}</span>
        <small>{s.assignee_label}
          {done ? ` · ${s.done_auto ? "ticked by DocVault" : `done${s.done_by ? ` by ${s.done_by}` : ""}`} ${fmt(s.done_at!)}` : s.skipped ? ` · skipped: ${s.note}` : s.kind === "auto" ? ` · ticks itself: ${s.auto_label.charAt(0).toLowerCase()}${s.auto_label.slice(1)}` : s.kind === "read" ? " · ticks itself when acknowledged" : s.kind === "review" ? " · ticks itself when reviewed or revised" : ""}
          {s.answer === "not_yet" && !done ? ` · not yet: ${s.note}` : ""}
          {s.file_name ? <> · <button className="linkbtn" onClick={() => void api.wfStepFileUrl(s.id).then((r) => window.open(r.url, "_blank"))}>{s.file_name}</button></> : null}
          {s.form_url && !done ? <> · <a className="doclink" href={s.form_url} target="_blank" rel="noreferrer">Open the form</a></> : null}
        </small>
        {s.help && <small>{s.help}</small>}
        {!done && !s.skipped && s.can_tick && (
          <div className="wf-acts">
            {s.kind === "approval" && <><button className="btn btn-accent btn-sm" onClick={() => void run(() => api.wfStep(s.id, { answer: "yes" }), "Approved.")}>Yes</button><button className="btn btn-ghost btn-sm" onClick={() => setAsking("not_yet")}>Not yet</button></>}
            {s.kind === "upload" && <label className="btn btn-ghost btn-sm">Add the file<input type="file" hidden onChange={(e) => { void upload(e.target.files?.[0]); e.target.value = ""; }} /></label>}
            <button className="linkbtn" onClick={() => setAsking("skip")}>Skip</button>
          </div>
        )}
        {asking && (
          <div className="wf-acts">
            <input className="inp inp-sm" style={{ flex: 1 }} value={note} maxLength={500} placeholder={asking === "skip" ? "Why skip it?" : "What's still needed?"} onChange={(e) => setNote(e.target.value)} />
            <button className="btn btn-ghost btn-sm" disabled={!note.trim()} onClick={() => void run(() => api.wfStep(s.id, asking === "skip" ? { skipped: true, note } : { answer: "not_yet", note }), "Saved.").then(() => { setAsking(""); setNote(""); })}>Save</button>
            <button className="linkbtn" onClick={() => setAsking("")}>Cancel</button>
          </div>
        )}
      </div>
      <div className={`cap ${!done && !s.skipped && s.due_on && daysBetween(new Date().toISOString().slice(0, 10), s.due_on) < 0 ? "wf-late" : ""}`}>{s.due_on && !done && !s.skipped ? `Due ${fmt(s.due_on)}` : ""}</div>
    </div>
  );
}
